import { Link, useLinkProps, useRouterState } from "@tanstack/react-router";
import { CircleSmall } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { Skeleton } from "@/components/ui/skeleton";
import { useSidebarConversations } from "./useSidebarConversations";

const NAVIGATION_FOCUS_CLASS =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar";

const rowClass = (desktopGeometry: boolean, active: boolean) =>
  cn(
    "flex items-center gap-2 rounded-md px-3 text-xs leading-[18px] tracking-[0.18px] transition-colors",
    "text-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
    NAVIGATION_FOCUS_CLASS,
    desktopGeometry ? "h-[26px]" : "py-2",
    active && "bg-sidebar-primary",
  );

const BrowseAllLink = ({
  desktopGeometry,
  onNavigate,
  children,
}: {
  desktopGeometry: boolean;
  onNavigate?: () => void;
  children: React.ReactNode;
}) => {
  const linkProps = useLinkProps({
    to: "/playground/{-$id}",
    params: { id: undefined },
    activeOptions: { exact: true },
    activeProps: {},
    onClick: onNavigate,
    className: cn(
      "flex min-w-0 items-center rounded-md px-3 text-start text-xs font-medium leading-[18px] tracking-[0.18px] whitespace-normal text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
      NAVIGATION_FOCUS_CLASS,
      desktopGeometry ? "min-h-[26px] py-1" : "py-2",
    ),
  });

  return (
    <a {...linkProps} aria-current={undefined}>
      <span className="min-w-0 break-words">{children}</span>
    </a>
  );
};

/**
 * Recent conversations, as a navigation list.
 *
 * Deliberately not a second copy of Playground's sidebar. That one searches,
 * groups by date, renames and deletes; this one gets you back to a recent chat
 * and then hands over to "browse all". Rename and delete stay where the full
 * list is, so there is one implementation of each.
 *
 * It also cannot reuse Playground's list item: that component reads
 * `usePlaygroundRouting`, which throws outside the Playground routing provider,
 * and pulls in MUI's `useTheme`. The active row is derived from the router's own
 * pathname instead, which works anywhere in the tree.
 */
export const SidebarConversations = ({
  collapsed,
  desktopGeometry,
  onNavigate,
}: {
  collapsed: boolean;
  desktopGeometry: boolean;
  onNavigate?: () => void;
}) => {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Nothing to show in the icon rail, and no reason to spend a request on it.
  const { data, isLoading, isError } = useSidebarConversations(!collapsed);

  if (collapsed) return null;

  const activeId = pathname.startsWith("/playground/")
    ? pathname.slice("/playground/".length).split("/")[0]
    : undefined;

  if (isLoading) {
    return (
      <div className="space-y-1 px-3 py-1" aria-hidden="true">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-5 w-full rounded-md" />
        ))}
      </div>
    );
  }

  // A failed sidebar list must not look like "you have no conversations".
  if (isError) {
    return (
      <p className="px-3 py-1.5 text-xs text-sidebar-foreground/60">
        {t("conversation.failedToLoadConversations")}
      </p>
    );
  }

  const conversations = data ?? [];

  return (
    <div className={desktopGeometry ? "space-y-1" : "space-y-0.5"}>
      {conversations.length === 0 ? (
        <p className="px-3 py-1.5 text-xs text-sidebar-foreground/60">
          {t("conversation.noConversationsYet")}
        </p>
      ) : (
        conversations.map((conversation) => (
          <Link
            key={conversation.id}
            to="/playground/{-$id}"
            params={{ id: conversation.id }}
            activeOptions={{ exact: true }}
            onClick={onNavigate}
            title={conversation.name}
            aria-current={conversation.id === activeId ? "page" : undefined}
            className={rowClass(desktopGeometry, conversation.id === activeId)}
          >
            <CircleSmall
              className={cn(
                "size-4 shrink-0",
                conversation.id === activeId && "text-sidebar-primary-foreground",
              )}
              aria-hidden="true"
            />
            <span className="truncate">{conversation.name}</span>
          </Link>
        ))
      )}

      <div className="mx-3 border-t border-sidebar-border pt-2 pb-1">
        <BrowseAllLink
          desktopGeometry={desktopGeometry}
          onNavigate={onNavigate}
        >
          {t("conversation.browseAll")}
        </BrowseAllLink>
      </div>
    </div>
  );
};
